import Konva from "konva";
import type { Vector2d } from "konva/lib/types";

export enum Tool { None = "None", Move = "Move", Drag = "Drag", Draw = "Draw", Clear = "Clear", Place = "Place", Text = "Text" }
export enum Placement { None = "None", Center = "Center", Corner = "Corner" }

export interface Toolbar { selectedTool: Tool; gridMagnet: Placement; }
export interface BeadTemplate { id: number; name: string; radius: number; fillColor: string; strokeColor: string; strokeWidth: number; }
export interface Grid { cellSize: { height: number; width: number }; isVisible: boolean; fillColor: string; position: { x: number; y: number }; }

export class Canvas2D {
    private stage: Konva.Stage;
    private gridLayer: Konva.Layer;
    private beadLayer: Konva.Layer;
    private textLayer: Konva.Layer;
    private uiLayer: Konva.Layer;
    private transformer: Konva.Transformer;
    private toolbar: Toolbar = { selectedTool: Tool.None, gridMagnet: Placement.None };
    private grid: Grid = { cellSize: { height: 20, width: 20 }, isVisible: true, fillColor: "#888888", position: { x: 0.0, y: 0.0 } };

    private defaultFontSize: number = 20;
    private defaultTextColor: string = "#000000";

    private selectedTemplateId: number | null = null;
    private isPointerDown = false;
    private isPanning = false;
    private lastPanPosition: Vector2d | null = null;
    // Tracks the last placed bead position for free-form (no-snap) draw throttling
    private lastPlacedPosition: Vector2d | null = null;

    private activeTextarea: HTMLTextAreaElement | null = null;
    private activeTextareaCleanup: (() => void) | null = null;

    private constructor(private dotNetHelper: any, containerId: string = "container") {
        const container = document.getElementById(containerId);

        this.stage = new Konva.Stage({
            container: containerId,
            width: container?.offsetWidth || 800,
            height: container?.offsetHeight || 600,
        });

        // Templates are stored as a stage-level attribute so they survive toJSON() / loadProject()
        this.stage.setAttr('templates', []);

        // Named at construction so loadProject() can locate them reliably in the JSON tree
        this.gridLayer = new Konva.Layer({ name: 'gridLayer', listening: false });
        this.beadLayer = new Konva.Layer({ name: 'beadLayer' });
        this.textLayer = new Konva.Layer({ name: 'textLayer' });
        this.uiLayer = new Konva.Layer({ name: 'uiLayer' });

        this.transformer = new Konva.Transformer({
            enabledAnchors: ['middle-left', 'middle-right'],
            boundBoxFunc: function (oldBox, newBox) {
                newBox.width = Math.max(30, newBox.width);
                return newBox;
            },
            rotateEnabled: false,
        });
        this.uiLayer.add(this.transformer);

        this.stage.add(this.gridLayer, this.beadLayer, this.textLayer, this.uiLayer);

        this.setupInteractions();
        this.setupKeyboardShortcuts();
        this.drawGrid();
    }

    static create(dotNetHelper: any, containerId: string = "container"): Promise<Canvas2D> {
        return new Promise(resolve => {
            const instance = new Canvas2D(dotNetHelper, containerId);
            const container = document.getElementById(containerId);
            if (!container) {
                resolve(instance);
                return;
            }

            const observer = new ResizeObserver(entries => {
                const { width, height } = entries[0].contentRect;
                if (width > 0 && height > 0) {
                    instance.stage.width(width);
                    instance.stage.height(height);
                    instance.drawGrid();
                    // Resolve on first valid measurement — canvas is now truly ready.
                    resolve(instance);
                }
            });
            observer.observe(container);
        });
    }

    // -------------------------------------------------------------------------
    // TOOLBAR & TEMPLATE MANAGEMENT
    // -------------------------------------------------------------------------

    public updateToolbar(toolbar: Toolbar) {
        this.toolbar = toolbar;
        this.updateDraggability(); // Also calls updateCursor internally
    }

    public addTemplate(template: BeadTemplate) {
        const templates = this.getTemplates();
        templates.push(template);
        this.stage.setAttr('templates', templates);
        // Auto-select the very first template that arrives
        if (this.selectedTemplateId === null) {
            this.selectedTemplateId = template.id;
        }
    }

    public updateTemplate(template: BeadTemplate) {
        const templates = this.getTemplates();
        const index = templates.findIndex(t => t.id === template.id);
        if (index === -1) return;

        templates[index] = template;
        this.stage.setAttr('templates', templates);

        // Cascade style changes to every placed bead that uses this template
        this.beadLayer.find(`.${template.id}`).forEach(node => {
            this.applyTemplateStyle(node as Konva.Circle, template);
        });
    }

    public removeTemplate(id: number) {
        const templates = this.getTemplates().filter(t => t.id !== id);
        this.stage.setAttr('templates', templates);

        // Destroy all beads that were using the deleted template
        this.beadLayer.find(`.${id}`).forEach(node => node.destroy());

        // Ensure selectedTemplateId never points at a deleted template
        if (this.selectedTemplateId === id) {
            this.selectedTemplateId = templates.length > 0 ? templates[0].id : null;
        }
    }

    public selectTemplate(id: number) {
        this.selectedTemplateId = id;
    }

    public updateGrid(grid: Grid) {
        this.grid = grid;
        this.gridLayer.visible(grid.isVisible);
        this.drawGrid(); // Always redraw — clears old lines even when newly hidden
    }

    public setZoom(zoomValue: number) {
        this.stage.scale({ x: zoomValue, y: zoomValue });
        this.drawGrid();
        if (this.transformer.nodes().length > 0) {
            this.transformer.forceUpdate();
        }
    }

    // -------------------------------------------------------------------------
    // TEXT & SELECTION MANAGEMENT
    // -------------------------------------------------------------------------

    public setTextFontSize(fontSize: number) {
        this.defaultFontSize = fontSize;
        const selected = this.transformer.nodes()[0] as Konva.Text;
        if (selected && selected.className === 'Text') {
            selected.fontSize(fontSize);
            this.transformer.forceUpdate();
            this.textLayer.batchDraw();
            this.uiLayer.batchDraw();
            this.dotNetHelper.invokeMethodAsync('OnCanvasModified');
        }
    }

    public setTextColor(color: string) {
        this.defaultTextColor = color;
        const selected = this.transformer.nodes()[0] as Konva.Text;
        if (selected && selected.className === 'Text') {
            selected.fill(color);
            this.textLayer.batchDraw();
            this.dotNetHelper.invokeMethodAsync('OnCanvasModified');
        }
    }

    public selectTextNode(textNode: Konva.Text | null) {
        if (textNode) {
            this.transformer.nodes([textNode]);
            this.transformer.visible(true);
            this.uiLayer.batchDraw();
            this.dotNetHelper.invokeMethodAsync('OnTextSelected', textNode.fontSize(), textNode.fill()?.toString() || this.defaultTextColor);
        } else {
            this.transformer.nodes([]);
            this.transformer.visible(false);
            this.uiLayer.batchDraw();
        }
    }

    public createTextNode(pos: Vector2d, initialText: string = "Text"): Konva.Text {
        const textNode = new Konva.Text({
            id: `text_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
            name: 'textNode',
            text: initialText,
            x: pos.x,
            y: pos.y,
            fontSize: this.defaultFontSize,
            fontFamily: 'Segoe UI, Roboto, Helvetica, Arial, sans-serif',
            fill: this.defaultTextColor,
            padding: 4,
            width: 160,
            draggable: this.toolbar.selectedTool === Tool.Drag || this.toolbar.selectedTool === Tool.Text,
        });

        textNode.setAttr('isNew', true);
        this.registerTextHandlers(textNode);
        this.textLayer.add(textNode);
        this.textLayer.batchDraw();
        return textNode;
    }

    private registerTextHandlers(textNode: Konva.Text) {
        textNode.on('transform', () => {
            textNode.setAttrs({
                width: Math.max(30, textNode.width() * textNode.scaleX()),
                scaleX: 1,
                scaleY: 1,
            });
            this.dotNetHelper.invokeMethodAsync('OnCanvasModified');
        });

        textNode.on('dragstart', () => {
            if (this.toolbar.selectedTool === Tool.Drag || this.toolbar.selectedTool === Tool.Text) {
                this.stage.container().style.cursor = 'grabbing';
                this.selectTextNode(textNode);
            }
        });

        textNode.on('dragend', () => {
            this.stage.container().style.cursor = this.toolbar.selectedTool === Tool.Drag ? 'grab' : (this.toolbar.selectedTool === Tool.Text ? 'text' : 'default');
            this.dotNetHelper.invokeMethodAsync('OnCanvasModified');
        });

        textNode.on('click tap', (e) => {
            e.cancelBubble = true;
            if (this.toolbar.selectedTool === Tool.Clear) {
                this.selectTextNode(null);
                textNode.destroy();
                this.textLayer.batchDraw();
                this.dotNetHelper.invokeMethodAsync('OnCanvasModified');
                return;
            }

            if (this.toolbar.selectedTool === Tool.Text) {
                this.openTextEditor(textNode);
                return;
            }

            if (this.toolbar.selectedTool === Tool.Drag) {
                this.selectTextNode(textNode);
            }
        });

        textNode.on('dblclick dbltap', (e) => {
            e.cancelBubble = true;
            if (this.toolbar.selectedTool !== Tool.Clear) {
                this.openTextEditor(textNode);
            }
        });
    }

    private openTextEditor(textNode: Konva.Text) {
        if (this.activeTextareaCleanup) {
            this.activeTextareaCleanup();
        }

        this.selectTextNode(textNode);
        textNode.hide();
        this.transformer.hide();
        this.textLayer.batchDraw();
        this.uiLayer.batchDraw();

        const textPosition = textNode.absolutePosition();
        const stageBox = this.stage.container().getBoundingClientRect();
        const stageScale = this.stage.scaleX();

        const areaPosition = {
            x: stageBox.left + textPosition.x,
            y: stageBox.top + textPosition.y,
        };

        const textarea = document.createElement('textarea');
        document.body.appendChild(textarea);
        this.activeTextarea = textarea;

        const isNew = !!textNode.getAttr('isNew');
        textarea.value = textNode.text();
        textarea.style.position = 'absolute';
        textarea.style.top = areaPosition.y + 'px';
        textarea.style.left = areaPosition.x + 'px';
        textarea.style.width = Math.max(60, textNode.width() * stageScale - textNode.padding() * 2 * stageScale) + 'px';
        textarea.style.height = Math.max(30, textNode.height() * stageScale - textNode.padding() * 2 * stageScale + 5) + 'px';
        textarea.style.fontSize = (textNode.fontSize() * stageScale) + 'px';
        textarea.style.border = '1px dashed #0078d4';
        textarea.style.borderRadius = '2px';
        textarea.style.padding = (textNode.padding() * stageScale) + 'px';
        textarea.style.margin = '0px';
        textarea.style.overflow = 'hidden';
        textarea.style.background = 'rgba(255, 255, 255, 0.95)';
        textarea.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
        textarea.style.outline = 'none';
        textarea.style.resize = 'none';
        textarea.style.lineHeight = textNode.lineHeight().toString();
        textarea.style.fontFamily = textNode.fontFamily();
        textarea.style.transformOrigin = 'left top';
        textarea.style.textAlign = textNode.align();
        textarea.style.color = textNode.fill() ? textNode.fill().toString() : this.defaultTextColor;
        textarea.style.zIndex = '10000';

        const rotation = textNode.rotation();
        let transform = '';
        if (rotation) {
            transform += 'rotateZ(' + rotation + 'deg) ';
        }
        transform += 'translateY(-' + (2 * stageScale) + 'px)';
        textarea.style.transform = transform;

        const adjustHeight = () => {
            textarea.style.height = 'auto';
            textarea.style.height = (textarea.scrollHeight + 3) + 'px';
        };

        const adjustWidth = (newWidth = 0) => {
            if (!newWidth) {
                newWidth = Math.max(50, textarea.value.length * textNode.fontSize());
            }
            textarea.style.width = (newWidth * stageScale) + 'px';
        };

        adjustHeight();
        textarea.focus();
        if (isNew) {
            textarea.select();
        }

        let isCleanedUp = false;
        const removeTextarea = (commit: boolean = true) => {
            if (isCleanedUp) return;
            isCleanedUp = true;

            window.removeEventListener('click', handleOutsideClick);
            window.removeEventListener('touchstart', handleOutsideClick);
            window.removeEventListener('keydown', handleKeydown);

            if (textarea.parentNode) {
                textarea.parentNode.removeChild(textarea);
            }
            this.activeTextarea = null;
            this.activeTextareaCleanup = null;

            const val = textarea.value;
            textNode.setAttr('isNew', false);

            if (commit) {
                const trimmed = val.trim();
                if (trimmed.length === 0 && isNew) {
                    textNode.destroy();
                    this.selectTextNode(null);
                } else {
                    textNode.text(val);
                    textNode.show();
                    this.selectTextNode(textNode);
                }
                this.dotNetHelper.invokeMethodAsync('OnCanvasModified');
            } else {
                if (isNew) {
                    textNode.destroy();
                    this.selectTextNode(null);
                } else {
                    textNode.show();
                    this.selectTextNode(textNode);
                }
            }

            this.textLayer.batchDraw();
            this.uiLayer.batchDraw();
        };

        this.activeTextareaCleanup = () => removeTextarea(true);

        const handleKeydown = (e: KeyboardEvent) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                removeTextarea(true);
            } else if (e.key === 'Escape') {
                e.preventDefault();
                removeTextarea(false);
            }
        };

        textarea.addEventListener('keydown', handleKeydown);
        textarea.addEventListener('input', () => {
            adjustHeight();
            const scale = textNode.getAbsoluteScale().x;
            adjustWidth(textNode.width() * scale);
        });

        const handleOutsideClick = (e: MouseEvent | TouchEvent) => {
            if (e.target !== textarea) {
                removeTextarea(true);
            }
        };

        setTimeout(() => {
            if (!isCleanedUp) {
                window.addEventListener('click', handleOutsideClick);
                window.addEventListener('touchstart', handleOutsideClick);
            }
        });
    }

    private setupKeyboardShortcuts() {
        window.addEventListener('keydown', (e) => {
            if (e.key === 'Delete' || e.key === 'Backspace') {
                const activeTag = document.activeElement?.tagName.toLowerCase();
                if (activeTag === 'input' || activeTag === 'textarea') {
                    return;
                }
                const selectedNodes = this.transformer.nodes();
                if (selectedNodes.length > 0) {
                    selectedNodes.forEach(node => node.destroy());
                    this.selectTextNode(null);
                    this.textLayer.batchDraw();
                    this.dotNetHelper.invokeMethodAsync('OnCanvasModified');
                }
            }
        });
    }

    // -------------------------------------------------------------------------
    // INTERACTION SETUP
    // -------------------------------------------------------------------------

    private setupInteractions() {
        // Always clean up before attaching — prevents double-binding after loadProject
        this.stage.off('pointerdown pointermove pointerup mouseleave mouseenter click tap');

        this.stage.on('pointerdown', (e) => {
            this.isPointerDown = true;
            if (this.toolbar.selectedTool === Tool.Move) {
                this.isPanning = true;
                this.lastPanPosition = this.stage.getPointerPosition();
            } else if (this.toolbar.selectedTool === Tool.Place) {
                this.placeSingleBead();
            } else if (this.toolbar.selectedTool === Tool.Text) {
                // If a textarea was active, clicking outside will remove it; don't create another one
                if (this.activeTextarea) {
                    return;
                }
                // If clicked on stage empty area, create new text field
                if (e.target === this.stage) {
                    const pos = this.stage.getRelativePointerPosition();
                    if (pos) {
                        const textNode = this.createTextNode(pos, "Text");
                        this.openTextEditor(textNode);
                    }
                }
            } else {
                if (e.target === this.stage) {
                    this.selectTextNode(null);
                }
                this.handlePaintErase();
            }
        });

        this.stage.on('pointermove', () => {
            if (this.toolbar.selectedTool === Tool.Move && this.isPanning && this.lastPanPosition) {
                const pos = this.stage.getPointerPosition();
                if (pos) {
                    const positionX = this.stage.x() + (pos.x - this.lastPanPosition.x);
                    const positionY = this.stage.y() + (pos.y - this.lastPanPosition.y);
                    this.lastPanPosition = pos;
                    this.restorePosition(positionX, positionY);
                    this.notifyPositionChanged();
                }
            } else if (this.isPointerDown && this.toolbar.selectedTool !== Tool.Place && this.toolbar.selectedTool !== Tool.Text) {
                this.handlePaintErase();
            }
        });

        this.stage.on('pointerup mouseleave', () => {
            this.isPointerDown = false;
            this.isPanning = false;
            this.lastPanPosition = null;
            this.lastPlacedPosition = null; // Next stroke starts with no throttle memory
        });

        // Re-apply tool cursor when pointer re-enters — browsers reset it on mouseleave
        this.stage.on('mouseenter', () => this.updateCursor());
    }

    // -------------------------------------------------------------------------
    // PAINT / ERASE
    // -------------------------------------------------------------------------

    private notifyPositionChanged() {
        const { x, y } = this.stage.position();
        this.dotNetHelper.invokeMethodAsync('OnCanvasPositionChanged', x, y);
    }

    private handlePaintErase() {
        // getRelativePointerPosition already accounts for stage scale and position,
        // returning world-space coordinates — no further conversion needed
        const pos = this.stage.getRelativePointerPosition();
        if (!pos) return;

        if (this.toolbar.selectedTool === Tool.Draw && this.selectedTemplateId !== null) {
            this.placeOrReplaceBead(pos, this.selectedTemplateId);
        } else if (this.toolbar.selectedTool === Tool.Clear) {
            // Erase uses a radius hit-test so it finds any bead the cursor touches,
            // regardless of which snapping mode was active when the bead was placed.
            const hit = this.getBeadAtPointer(pos);
            if (hit) {
                hit.destroy();
                // Notify Blazor that the canvas content has changed
                this.dotNetHelper.invokeMethodAsync('OnCanvasModified');
            } else {
                const hitText = this.getTextAtPointer(pos);
                if (hitText) {
                    this.selectTextNode(null);
                    hitText.destroy();
                    this.textLayer.batchDraw();
                    this.dotNetHelper.invokeMethodAsync('OnCanvasModified');
                }
            }
        }
    }

    /**
     * Place tool: places exactly one bead at the current pointer position.
     * Unlike Draw, holding the mouse button down does NOT paint additional beads.
     * The throttle guard (lastPlacedPosition) is intentionally bypassed — the user
     * clicked deliberately, so we always honour the intent.
     */
    private placeSingleBead() {
        if (this.selectedTemplateId === null) return;
        const pos = this.stage.getRelativePointerPosition();
        if (!pos) return;

        const saved = this.lastPlacedPosition;
        this.lastPlacedPosition = null;
        this.placeOrReplaceBead(pos, this.selectedTemplateId);
        this.lastPlacedPosition = saved;
    }

    private placeOrReplaceBead(pos: Vector2d, templateId: number) {
        const snappedPos = this.snapToGrid(pos);

        // Free-form mode (no snapping): throttle by bead diameter so dragging
        // the mouse doesn't flood the canvas with overlapping circles.
        if (this.toolbar.gridMagnet === Placement.None && this.lastPlacedPosition) {
            const template = this.getTemplates().find(t => t.id === templateId);
            const minDist = template ? template.radius * 2 : this.grid.cellSize.width;
            const dx = snappedPos.x - this.lastPlacedPosition.x;
            const dy = snappedPos.y - this.lastPlacedPosition.y;
            if (dx * dx + dy * dy < minDist * minDist) return;
        }

        const existing = this.getBeadsAtPosition(snappedPos);

        if (existing.length > 0) {
            const existingBead = existing[0] as Konva.Circle;
            if (existingBead.getAttr('templateId') === templateId) return; // Same — skip
            existingBead.destroy(); // Different — replace
        }

        const template = this.getTemplates().find(t => t.id === templateId);
        if (!template) return;

        const bead = new Konva.Circle({
            id: `bead_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
            name: template.id.toString(), // dot-selector: beadLayer.find(`.${id}`)
            templateId: template.id,      // custom attr for identity checks
            x: snappedPos.x,
            y: snappedPos.y,
            draggable: this.toolbar.selectedTool === Tool.Drag,
        });

        this.applyTemplateStyle(bead, template);
        this.registerDragHandlers(bead);
        this.beadLayer.add(bead);
        this.lastPlacedPosition = snappedPos;

        // Notify Blazor that the canvas content has changed
        this.dotNetHelper.invokeMethodAsync('OnCanvasModified');
    }

    private registerDragHandlers(bead: Konva.Circle) {
        bead.on('dragstart', e => {
            // Snapshot position in case we need to revert (same-template collision)
            e.target.setAttr('originalPosition', { ...e.target.position() });
            this.stage.container().style.cursor = 'grabbing';
        });

        bead.on('dragend', e => {
            const snappedPos = this.snapToGrid(e.target.position());
            const templateId = e.target.getAttr('templateId');

            // Exclude the dragging bead itself from the collision check
            const overlapping = this.getBeadsAtPosition(snappedPos).filter(b => b !== e.target);

            if (overlapping.length > 0) {
                const other = overlapping[0] as Konva.Circle;
                if (other.getAttr('templateId') === templateId) {
                    // Same template at target — revert to where the drag started
                    e.target.position(e.target.getAttr('originalPosition'));
                    // No change notification — position reverted, canvas unchanged
                    this.stage.container().style.cursor = 'grab';
                    return;
                } else {
                    // Different template at target — destroy it and snap here
                    other.destroy();
                    e.target.position(snappedPos);
                }
            } else {
                e.target.position(snappedPos);
            }

            this.stage.container().style.cursor = 'grab';

            // Notify Blazor that a bead was successfully moved
            this.dotNetHelper.invokeMethodAsync('OnCanvasModified');
        });
    }

    private updateDraggability() {
        const isDragTool = this.toolbar.selectedTool === Tool.Drag;
        this.beadLayer.getChildren().forEach(child => child.draggable(isDragTool));
        this.textLayer.getChildren().forEach(child => child.draggable(isDragTool || this.toolbar.selectedTool === Tool.Text));
        if (this.toolbar.selectedTool !== Tool.Drag && this.toolbar.selectedTool !== Tool.Text) {
            this.selectTextNode(null);
        }
        this.updateCursor();
    }

    private updateCursor() {
        const container = this.stage.container();

        // The OS 'crosshair' cursor is white on some platforms/backgrounds.
        // A custom SVG cursor with a dark stroke + light inner line is always readable.
        const crosshairSvg = `
            <svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'>
                <line x1='16' y1='2'  x2='16' y2='30' stroke='black' stroke-width='3'/>
                <line x1='2'  y1='16' x2='30' y2='16' stroke='black' stroke-width='3'/>
                <line x1='16' y1='2'  x2='16' y2='30' stroke='white' stroke-width='1'/>
                <line x1='2'  y1='16' x2='30' y2='16' stroke='white' stroke-width='1'/>
            </svg>`;
        const crosshairUrl = `url("data:image/svg+xml,${encodeURIComponent(crosshairSvg)}") 16 16, crosshair`;

        const placeSvg = `
            <svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'>
                <line x1='16' y1='2'  x2='16' y2='30' stroke='black' stroke-width='3'/>
                <line x1='2'  y1='16' x2='30' y2='16' stroke='black' stroke-width='3'/>
                <line x1='16' y1='2'  x2='16' y2='30' stroke='white' stroke-width='1'/>
                <line x1='2'  y1='16' x2='30' y2='16' stroke='white' stroke-width='1'/>
                <circle cx='16' cy='16' r='3' fill='black'/>
                <circle cx='16' cy='16' r='2' fill='white'/>
            </svg>`;
        const placeUrl = `url("data:image/svg+xml,${encodeURIComponent(placeSvg)}") 16 16, crosshair`;

        switch (this.toolbar.selectedTool) {
            case Tool.Move: container.style.cursor = 'move'; break;
            case Tool.Drag: container.style.cursor = 'grab'; break;
            case Tool.Draw: container.style.cursor = crosshairUrl; break;
            case Tool.Place: container.style.cursor = placeUrl; break;
            case Tool.Clear: container.style.cursor = 'cell'; break;
            case Tool.Text: container.style.cursor = 'text'; break;
            default: container.style.cursor = 'default';
        }
    }

    // -------------------------------------------------------------------------
    // GRID
    // -------------------------------------------------------------------------

    private drawGrid() {
        this.gridLayer.destroyChildren();
        if (!this.grid?.isVisible) return;

        const stepX = this.grid.cellSize.width;
        const stepY = this.grid.cellSize.height;

        const positionX = this.grid.position.x;
        const positionY = this.grid.position.y;
        this.stage.position({ x: positionX, y: positionY });

        // Paint a single grid cell onto an off-screen canvas and tile it as a
        // fill pattern. This is O(1) regardless of canvas size, unlike drawing
        // individual Line nodes which scales with visible area.
        const patternCanvas = document.createElement('canvas');
        patternCanvas.width = stepX;
        patternCanvas.height = stepY;
        const ctx = patternCanvas.getContext('2d');
        if (ctx) {
            ctx.strokeStyle = this.grid.fillColor;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(0, 0); ctx.lineTo(stepX, 0); // top border of cell
            ctx.moveTo(0, 0); ctx.lineTo(0, stepY);  // left border of cell
            ctx.stroke();
        }

        const scaleX = this.stage.scaleX();
        const scaleY = this.stage.scaleY();

        // Convert the visible viewport into world coordinates
        const viewportWidth = this.stage.width() / scaleX;
        const viewportHeight = this.stage.height() / scaleY;
        const startX = -positionX / scaleX;
        const startY = -positionY / scaleY;

        // Snap the rect origin to the nearest grid line so the pattern never
        // drifts or "jumps" during panning or zooming
        const snapStartX = Math.floor(startX / stepX) * stepX;
        const snapStartY = Math.floor(startY / stepY) * stepY;

        this.gridLayer.add(new Konva.Rect({
            x: snapStartX,
            y: snapStartY,
            width: viewportWidth + stepX * 2, // +1 cell buffer prevents edge flash
            height: viewportHeight + stepY * 2,
            fillPatternImage: patternCanvas as unknown as HTMLImageElement,
            fillPatternRepeat: 'repeat',
            listening: false,
        }));
    }

    public resetPosition() {
        this.restorePosition(0, 0);
    }

    public restorePosition(x: number, y: number) {
        this.grid.position.x = x;
        this.grid.position.y = y;
        this.drawGrid();
    }

    // -------------------------------------------------------------------------
    // FILE I/O
    // -------------------------------------------------------------------------

    public loadProject(json: string) {
        this.selectTextNode(null);
        if (this.activeTextareaCleanup) {
            this.activeTextareaCleanup();
        }

        // Clear placed beads, text, and the grid; keep the stage and its layers intact
        this.beadLayer.destroyChildren();
        this.textLayer.destroyChildren();
        this.gridLayer.destroyChildren();

        const stageData = JSON.parse(json) as { attrs: Record<string, any>; children: any[] };

        // Restore templates from the stage-level attribute
        const templates: BeadTemplate[] = stageData.attrs?.templates ?? [];
        this.stage.setAttr('templates', templates);
        this.selectedTemplateId = templates.length > 0 ? templates[0].id : null;

        // Reconstruct bead circles from the serialized bead layer.
        // We rebuild them manually so we can re-attach drag handlers cleanly.
        const beadLayerData = stageData.children?.find(
            (l: any) => l.attrs?.name === 'beadLayer'
        );
        if (beadLayerData?.children) {
            beadLayerData.children.forEach((nodeData: any) => {
                const bead = new Konva.Circle({
                    ...nodeData.attrs,
                    draggable: this.toolbar.selectedTool === Tool.Drag,
                });
                this.registerDragHandlers(bead);
                this.beadLayer.add(bead);
            });
        }

        // Reconstruct text nodes from serialized text layer
        const textLayerData = stageData.children?.find(
            (l: any) => l.attrs?.name === 'textLayer'
        );
        if (textLayerData?.children) {
            textLayerData.children.forEach((nodeData: any) => {
                if (nodeData.className === 'Text') {
                    const textNode = new Konva.Text({
                        ...nodeData.attrs,
                        draggable: this.toolbar.selectedTool === Tool.Drag || this.toolbar.selectedTool === Tool.Text,
                    });
                    this.registerTextHandlers(textNode);
                    this.textLayer.add(textNode);
                }
            });
        }

        // Re-attach stage events (setupInteractions cleans up first to avoid doubles)
        this.setupInteractions();
        this.drawGrid();
        this.textLayer.batchDraw();
        this.beadLayer.batchDraw();

        // Inform the Blazor toolbar so it can rebuild the template list UI
        this.dotNetHelper.invokeMethodAsync('OnTemplatesLoaded', templates);
    }

    public getProjectJson(): string {
        return this.stage.toJSON();
    }

    /**
     * Renders the canvas to a PNG data URL with the grid and UI/transformer handles hidden.
     * Returns the data URL directly — no callback needed.
     * Used for both user-facing image capture and cloud thumbnail generation.
     */
    public getImageDataUrl(pixelRatio: number = 1): string {
        const wasGridVisible = this.grid.isVisible;
        const wasUiVisible = this.uiLayer.visible();
        this.gridLayer.visible(false);
        this.uiLayer.visible(false);

        const dataUrl = this.stage.toDataURL({ mimeType: 'image/png', pixelRatio });

        this.gridLayer.visible(wasGridVisible);
        this.uiLayer.visible(wasUiVisible);
        return dataUrl;
    }

    public getImageDataUrlRestricted(minHeight: number, minWidth: number): string {
        const stageSize = this.stage.getSize()
        let pixelRatio = 1;
        const k_height = minHeight / stageSize.height;
        const k_width = minWidth / stageSize.width;

        if (k_height > k_width) {
            pixelRatio = k_height;
        }
        else {
            pixelRatio = k_width;
        }
        return this.getImageDataUrl(pixelRatio);
    }

    // -------------------------------------------------------------------------
    // UTILITIES
    // -------------------------------------------------------------------------

    /**
     * Snaps a world-space position to the nearest grid point.
     * Supports Center (bead sits in the middle of a cell) and Corner (bead sits
     * on the intersection of grid lines). Placement.None passes through unchanged.
     */
    private snapToGrid(pos: Vector2d): Vector2d {
        const stepX = this.grid.cellSize.width;
        const stepY = this.grid.cellSize.height;

        if (this.toolbar.gridMagnet === Placement.Center) {
            const offsetX = stepX / 2;
            const offsetY = stepY / 2;
            return {
                x: Math.round((pos.x - offsetX) / stepX) * stepX + offsetX,
                y: Math.round((pos.y - offsetY) / stepY) * stepY + offsetY,
            };
        }

        if (this.toolbar.gridMagnet === Placement.Corner) {
            return {
                x: Math.round(pos.x / stepX) * stepX,
                y: Math.round(pos.y / stepY) * stepY,
            };
        }

        return pos; // Placement.None — no snapping
    }

    /** Finds all beads at an exact world position. Works correctly after snapping. */
    private getBeadsAtPosition(pos: Vector2d): Konva.Node[] {
        return this.beadLayer.getChildren(node => node.x() === pos.x && node.y() === pos.y);
    }

    /**
     * Returns the topmost bead whose circle contains the given world-space position,
     * or null if the cursor is not over any bead.
     * Hit area = visual radius + half stroke-width, matching what the user sees.
     */
    private getBeadAtPointer(pos: Vector2d): Konva.Circle | null {
        let found: Konva.Circle | null = null;
        let minDistSq = Infinity;

        this.beadLayer.getChildren().forEach(node => {
            const bead = node as Konva.Circle;
            const dx = bead.x() - pos.x;
            const dy = bead.y() - pos.y;
            const distSq = dx * dx + dy * dy;

            // Include the stroke so the hit area matches the visible edge of the circle
            const hitRadius = bead.radius() + bead.strokeWidth() / 2;

            if (distSq <= hitRadius * hitRadius && distSq < minDistSq) {
                minDistSq = distSq;
                found = bead;
            }
        });

        return found;
    }

    private getTextAtPointer(pos: Vector2d): Konva.Text | null {
        let found: Konva.Text | null = null;
        this.textLayer.getChildren().forEach(node => {
            const textNode = node as Konva.Text;
            const x = textNode.x();
            const y = textNode.y();
            const w = textNode.width();
            const h = textNode.height();
            if (pos.x >= x && pos.x <= x + w && pos.y >= y && pos.y <= y + h) {
                found = textNode;
            }
        });
        return found;
    }

    private getTemplates(): BeadTemplate[] {
        return this.stage.getAttr('templates') ?? [];
    }

    private applyTemplateStyle(bead: Konva.Circle, template: BeadTemplate) {
        bead.fill(template.fillColor);
        bead.stroke(template.strokeColor);
        bead.strokeWidth(template.strokeWidth);
        bead.radius(template.radius - template.strokeWidth / 2);
    }
}

export function initCanvas2D(dotNetHelper: any): Promise<Canvas2D> {
    return Canvas2D.create(dotNetHelper);
}

export function triggerDownload(fileName: string, content: string, isDataUrl: boolean): void {
    const a = document.createElement('a');

    if (isDataUrl) {
        a.href = content;
    } else {
        const blob = new Blob([content], { type: 'application/json' });
        a.href = URL.createObjectURL(blob);
    }

    a.download = fileName;
    a.click();

    if (!isDataUrl) {
        URL.revokeObjectURL(a.href);
    }
}

/**
 * Shows or hides the browser "Leave site?" confirmation dialog when the user
 * tries to close or navigate away from the tab.
 * Call with true when there are unsaved changes, false after save or fresh load.
 */
export function setBeforeUnloadWarning(enabled: boolean): void {
    if (enabled) {
        window.onbeforeunload = () => '';
    } else {
        window.onbeforeunload = null;
    }
}

export function showConfirm(message: string): boolean {
    return window.confirm(message);
}
